import { AdminPermissions, User, UserRole } from '../types';

export type AdminPermissionFlag = {
  [K in keyof AdminPermissions]: AdminPermissions[K] extends boolean ? K : never;
}[keyof AdminPermissions];

export type AdminPermissionLimit = Exclude<keyof AdminPermissions, AdminPermissionFlag>;

/**
 * Whether `user` may use `flag`. Only a plain Admin is ever restricted —
 * a Super Admin always may, and other roles are governed by their own
 * role checks, not by this.
 *
 * A UX convenience only: it decides what to show. The server checks the
 * same switches independently on every request.
 */
export function hasAdminPermission(user: User | null | undefined, flag: AdminPermissionFlag): boolean {
  if (!user || user.role !== UserRole.ADMIN) return true;
  return user.adminPermissions?.[flag] !== false;
}

/** The Admin's limit for `key`, or null for none (always null for non-Admins). */
export function adminLimit(user: User | null | undefined, key: AdminPermissionLimit): number | null {
  if (!user || user.role !== UserRole.ADMIN) return null;
  return user.adminPermissions?.[key] ?? null;
}

// Drives the Super Admin's editor, in the order shown.
export const ADMIN_PERMISSION_FLAGS: Array<{ key: AdminPermissionFlag; label: string; description: string }> = [
  { key: 'resetPassword', label: 'Reset Password', description: "Generate a temporary password for another user." },
  { key: 'disableAccount', label: 'Disable Account', description: 'Disable and re-enable user accounts.' },
  { key: 'deleteAccount', label: 'Delete Account', description: 'Request deletion of Student accounts.' },
  { key: 'assignRoles', label: 'Assign Admin Roles', description: "Change a user's role, or register staff accounts." },
  { key: 'registerUser', label: 'Register New User', description: 'Create accounts from User Management.' },
  { key: 'renewalRequests', label: 'Renewal Requests', description: 'View and decide renewal requests.' },
  { key: 'applicationReports', label: 'Application Reports', description: 'Open the Reports page and export it.' },
  { key: 'editSignatories', label: 'Editable Signatories', description: 'Change the names and titles on the payroll.' },
  { key: 'themes', label: 'Themes', description: 'Choose a colour theme other than the default.' }
];

export const ADMIN_PERMISSION_LIMITS: Array<{ key: AdminPermissionLimit; label: string; description: string }> = [
  {
    key: 'maxPrograms',
    label: 'Programs allowed',
    description: 'How many scholarship programs this Admin may have at once. Leave empty for no limit; 0 stops them creating any.'
  },
  {
    key: 'announcementsPerDay',
    label: 'Announcements per day',
    description: 'How many announcements this Admin may post each day. Leave empty for no limit; 0 stops them posting.'
  }
];
